import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { combineLatest } from 'rxjs';
import { AlarmService } from '../../services/alarm.service';
import { AuthService } from '../../services/auth.service';
import { DeviceProfileService } from '../../services/device-profile.service';
import { DeviceService } from '../../services/device.service';
import { LogService } from '../../services/log.service';
import { PageLoaderService } from '../../services/page-loader.service';
import { ButtonIconComponent } from '../../shared/components/button-icon/button-icon.component';
import { DialogBase } from '../../shared/components/dialogs/dialog-base/dialog-base';
import { DialogConfirm } from '../../shared/components/dialogs/dialog-confirm/dialog-confirm';
import { InputSelectComponent } from '../../shared/components/input-select/input-select';
import { InputTextComponent } from '../../shared/components/input-text/input-text.component';
import { LastActivity } from '../../shared/components/last-activity/last-activity';
import { TableComponent } from '../../shared/components/table/table';
import { TranslateSafePipe } from '../../shared/pipes/translation.pipe';
import { formatTimeAgo } from '../../shared/utils/date.util';
import { AccountRole } from '../../types/dtos/account.dto';
import { BaseResponseDto } from '../../types/dtos/base.dto';
import { DeviceDto, DeviceRow} from '../../types/dtos/device.dto';
import { DeviceProfileDto } from '../../types/dtos/deviceProfile.dto';
import { DeviceStatus } from '../../types/enums/device.enum';
import { LoggingActionEnum, LoggingTargetEnum } from '../../types/enums/logging.enum';
import { InputSelectInterface, InputTextInterface } from '../../types/interfaces/input.interface';
import { TableConfig, TableRowDto, TableTagSeverity } from '../../types/interfaces/table.interface';
import { DashboardBoxComponent } from '../dashboard/dashboard-box/dashboard-box.component';
import { DialogDeviceDetail } from './dialog-device-detail/dialog-device-detail';
import { DialogEditDevice } from './dialog-edit-device/dialog-edit-device';

@Component({
  selector: 'app-devices',
  imports: [
    TableModule,
    SkeletonModule,
    DashboardBoxComponent,
    FormsModule,
    DialogEditDevice,
    DialogBase,
    DialogDeviceDetail,
    TableComponent,
    InputSelectComponent,
    InputTextComponent,
    ButtonIconComponent,
    LastActivity,
    TranslatePipe,
    TranslateSafePipe,
    DialogConfirm,
  ],
  templateUrl: './devices.component.html',
  styleUrl: './devices.component.css',
})
export class DevicesComponent implements AfterViewInit {
  public loading: boolean = true;
  public allDevices: DeviceDto[] = [];
  public deviceList: DeviceDto[] = [];
  public filteredDeviceCount: number = 0;
  public totalDeviceCount: number = 0;
  public isEditDialogOpen: boolean = false;
  public isDeviceDetailOpen: boolean = false;
  public isDeleteDialogOpen: boolean = false;

  public tableForm: FormGroup = new FormGroup({
    device_search: new FormControl(),
  });

  public tableRows: TableRowDto<DeviceRow>[] = [];
  public tableConfig: TableConfig<DeviceRow> = {
    columns: [
      {
        key: 'status',
        isTranslated: true,
        title: 'common.table.status.label',
        isTag: true,
        getTagColor: (row: DeviceRow) => row.status === 'online'
          ? TableTagSeverity.SUCCESS
          : TableTagSeverity.DANGER,
        overrideValue: (row: DeviceRow) => row.status === 'online' ? 'Online' : 'Offline',
        sticky: 'left',
      },
      {
        key: 'serial_number',
        isTranslated: true,
        title: 'common.table.serielNumber.label',
      },
      {
        key: 'device_eui',
        isTranslated: true,
        title: 'common.table.deviceEui.label',
        visible: false,
      },
      {
        key: 'model',
        isTranslated: true,
        title: 'common.table.model.label',
      },
      {
        key: 'address',
        isTranslated: true,
        title: 'common.table.address.label',
      },
      {
        key: 'alarms',
        isTranslated: true,
        title: 'common.table.alarms.label',
        isTag: true,
        getTagColor: (row: DeviceRow) => row.alarms > 0 ? TableTagSeverity.DANGER : TableTagSeverity.SUCCESS,
        sort: (a: DeviceRow, b: DeviceRow, order: number) => order * (a.alarms - b.alarms),
      },
      {
        key: 'last_measurement',
        isTranslated: true,
        title: 'common.table.lastMeasurement.label',
        overrideValue: (row: DeviceRow) => {
          if (!row.last_measurement) {
            return '-';
          }

          let lastMeasurementTime = formatTimeAgo(row.last_measurement_received_time);

          if (!lastMeasurementTime || lastMeasurementTime === '-') {
            return `${(row.last_measurement as number) / 1000} m³`;
          }

          lastMeasurementTime = lastMeasurementTime.charAt(0)
            .toLowerCase() + lastMeasurementTime.slice(1);

          return `${(row.last_measurement as number) / 1000} m³ ${lastMeasurementTime}`;
        },
        sort: (
          a: DeviceRow,
          b: DeviceRow,
          order: number,
        ) => order * ((a.last_measurement as number) - (b.last_measurement as number)),
      },
    ],
    mobile: {
      title: {
        key: 'serial_number',
        tag: 'Status',
        getTagColor: (row: DeviceRow) => row.status === 'online'
          ? TableTagSeverity.SUCCESS
          : TableTagSeverity.DANGER,
        overrideTag: (row: DeviceRow) => row.status === 'online' ? 'Online' : 'Offline',
      },
      description: {
        key: 'model',
      },
      sections: [
        {
          key: 'address',
          isTranslated: true,
          title: 'common.fields.address',
        },
        {
          key: 'last_measurement',
          isTranslated: true,
          title: 'common.fields.last-measurement',
          overrideValue: (row: DeviceRow) => {
            if (!row.last_measurement) {
              return '-';
            }

            let lastMeasurementTime = formatTimeAgo(row.last_measurement_received_time);

            if (!lastMeasurementTime || lastMeasurementTime === '-') {
              return `${(row.last_measurement as number) / 1000} m³`;
            }

            lastMeasurementTime = lastMeasurementTime.charAt(0)
              .toLowerCase() + lastMeasurementTime.slice(1);

            return `${(row.last_measurement as number) / 1000} m³ ${lastMeasurementTime}`;
          },
        },
        {
          key: 'alarms',
          isTranslated: true,
          title: 'common.fields.alarms',
          isTag: true,
          getTagColor: (row: DeviceRow) => row.alarms > 0 ? TableTagSeverity.DANGER : TableTagSeverity.SUCCESS,
        },
      ],
    },
    pagination: true,
    showRows: 25,
    rowsPerPageOptions: [
      25,
      50,
      100,
    ],
    scrollHeight: '55vh',
    emptyMessage: 'common.table.empty-message.device',
    lazy: false,
    sortableColumns: true,
    actions: [
      {
        icon: 'visibility',
        action: (row: DeviceRow) => {
          const device: DeviceDto | undefined = this.deviceList.find((deviceItem: DeviceDto) =>
            deviceItem.id === row.id,
          );

          if (!device) {
            return;
          }

          this.toggleViewDialog(device);
        },
      },
      {
        icon: 'edit',
        className: 'yellow',
        hidden: () => !this.hasAccess(AccountRole.OPERATOR),
        action: (row: DeviceRow) => {
          if (!this.hasAccess(AccountRole.OPERATOR)) return;

          const device: DeviceDto | undefined = this.deviceList.find((deviceItem: DeviceDto) =>
            deviceItem.id === row.id,
          );

          if (!device) return;

          this.toggleEditDialog(device);
        },
      },
      {
        icon: 'delete',
        className: 'red',
        hidden: () => !this.hasAccess(AccountRole.ADMIN),
        action: (row: DeviceRow) => {
          if (!this.hasAccess(AccountRole.ADMIN)) return;

          const device: DeviceDto | undefined = this.deviceList.find((deviceItem: DeviceDto) =>
            deviceItem.id === row.id,
          );

          console.log('device', device);

          if (!device) return;

          this.selectedDevice = device;
          this.isDeleteDialogOpen = true;
        },
      },
    ],
  };

  public deviceFilterForm: FormGroup = new FormGroup({
    statusSelect: new FormControl(),
    modelSelect: new FormControl(),
    search: new FormControl(),
  });

  public statusSelect: InputSelectInterface = {
    formGroup: this.deviceFilterForm,
    onInput: () => this._filterData(),
    isTranslated: true,
    label: 'common.input.status-select.label',
    id: 'statusSelect',
    iconLeft: 'account_circle',
    showClear: true,
    options: Object.values(DeviceStatus)
      .sort()
      .map((action) => ({
        isTranslated: true,
        label: 'common.enum.status.' + action,
        value: action.toLowerCase(),
      })),
    required: false,
  };

  public modelSelect: InputSelectInterface = {
    formGroup: this.deviceFilterForm,
    onInput: () => this._filterData(),
    isTranslated: true,
    label: 'common.input.model.label',
    id: 'modelSelect',
    iconLeft: 'account_circle',
    showClear: true,
    options: [],
    required: false,
  };

  public searchConfig: InputTextInterface = {
    formGroup: this.deviceFilterForm,
    isTranslated: true,
    label: 'common.input.search-device.label',
    id: 'search',
    iconLeft: 'search',
    showClear: true,
    type: 'text',
    onInput: () => this._filterData(),
  };

  public selectedDevice: DeviceDto | null = null;
  protected readonly AccountRole = AccountRole;
  private _destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private readonly _deviceService: DeviceService,
    private readonly _alarmService: AlarmService,
    private readonly _loggingService: LogService,
    private readonly _deviceProfileService: DeviceProfileService,
    private readonly _authService: AuthService,
    private readonly _route: ActivatedRoute,
    private readonly _pageLoaderService: PageLoaderService,
    private readonly _messageService: MessageService,
    private readonly _translateService: TranslateService,
  ) {
  }

  public getDeviceCount(): number {
    return this.deviceList.length;
  }

  public getDeviceTotal(): number {
    return this.allDevices.length;
  }

  public getDeviceNumber(device: DeviceDto): string {
    return this._deviceService.getDeviceNumberByDevice(device);
  }

  /**
   * Resets the filter form and updates the log data.
   */
  public resetFilter(): void {
    this.deviceFilterForm.reset();
    this._filterData();
  }

  public ngAfterViewInit(): void {
    combineLatest([
      this._deviceService.devices$,
      this._route.queryParamMap,
    ])
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: ([data, queryParams]: [DeviceDto[], ParamMap]) => {
          this.allDevices = data;
          this._filterData();

          this.loading = false;
          this.openEditDialogFromQueryParams(queryParams)
            .then();
        },
        error: (error: unknown) => {
          console.error('Error fetching devices:', error);
        },
      });

    this._deviceProfileService.devicesProfile$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((devicesProfile: DeviceProfileDto[]) => {
        this.modelSelect.options = [
          {
            isTranslated: true,
            label: 'common.label.no-models',
            value: '-1',
          },
          ...devicesProfile
            .filter((profile: DeviceProfileDto) => profile.model)
            .map((profile: DeviceProfileDto) => ({
              label: profile.model ?? '',
              value: profile.id.toString(),
            })),
        ];
      });

  }

  public getDeviceModelName(deviceProfileId: number): string {
    const deviceProfile: DeviceProfileDto | null =
      this._deviceProfileService.getDeviceProfileById(deviceProfileId);

    return deviceProfile?.model ?? '-';
  }

  public toggleEditDialog(device: DeviceDto | null): void {
    if (device) {
      this.selectedDevice = device;
      this.isDeviceDetailOpen = false;
      this.isEditDialogOpen = true;
      return;
    }

    this.isEditDialogOpen = false;
    this.selectedDevice = null;
  }

  public readonly closeEditDialog = (): void => {
    this.toggleEditDialog(null);
  };

  public toggleViewDialog(device: DeviceDto | null): void {
    if (!device) return;

    this._loggingService.sendLog({
      target: LoggingTargetEnum.DEVICE,
      action: LoggingActionEnum.VIEW,
      target_id: device.id,
    });

    this.selectedDevice = device;
    this.isEditDialogOpen = false;
    this.isDeviceDetailOpen = true;
  }

  public readonly closeShowDetails = (): void => {
    this.isDeviceDetailOpen = false;
    this.selectedDevice = null;
  };

  public hasAccess(role: AccountRole): boolean {
    return this._authService.canAccess(role);
  }

  private async openEditDialogFromQueryParams(queryParams: ParamMap): Promise<void> {
    const deviceId: number | null = Number(queryParams.get('id'));
    if (!deviceId || Number.isNaN(deviceId) || deviceId <= 0) return;
    const isEdit: boolean = queryParams.get('edit') === 'true';

    const device: DeviceDto | undefined = this.deviceList.find((deviceItem: DeviceDto) =>
      deviceItem.id === deviceId,
    );

    if (!device) return;

    await this._pageLoaderService.waitLoadingFinish();
    if (this.isEditDialogOpen || this.isDeviceDetailOpen) return;

    this.tableForm.patchValue({
      device_search: device.device_eui,
    });

    if (isEdit) {
      if (!this.hasAccess(AccountRole.OPERATOR)) return;

      this.toggleEditDialog(device);
    } else {
      this.toggleViewDialog(device);
    }
  }

  private _setTableRows(): void {
    this.tableRows = this.deviceList.map((device: DeviceDto) => {
      let meterValueKey: string = this._deviceService.getWaterMeasurementValueKey(device);
      return {
        id: device.id,
        status: device.status,
        device_eui: device.device_eui,
        serial_number: device.serial_number ?? '-',
        model: device.device_profile_id ? this.getDeviceModelName(device.device_profile_id) : '-',
        address: device.position?.address ?? '-',
        alarms: this._alarmService.getActiveAlarms(device).length,
        last_measurement: device.last_measurement?.[meterValueKey]
          ? Number(device.last_measurement[meterValueKey])
          : null,
        last_measurement_received_time: device.last_measurement_received_time ?? undefined,
        last_sigi_send_time: device.last_sigi_send_time ?? undefined,
      };
    });
  }

  private _filterData(): void {
    let {
      statusSelect,
      modelSelect,
      search,
    } = this.deviceFilterForm.value;

    modelSelect = Number(modelSelect);

    const normalizedSearch: string = String(search ?? '')
      .trim()
      .toLowerCase();

    this.deviceList = this.allDevices.filter((device: DeviceDto) => {
      const matchesStatus: boolean = !statusSelect || device.status === statusSelect;
      const matchesModel: boolean = !modelSelect || (device.device_profile_id ?? -1) === modelSelect;

      const deviceEui: string = device.device_eui ?? '';
      const serialNumber: string = device.serial_number ?? '';
      const model: string = device.device_profile_id
        ? this.getDeviceModelName(device.device_profile_id)
        : '-';
      const address: string = device.position?.address ?? '-';
      const alarmsCount: string = this._alarmService.getActiveAlarms(device)
        .length
        .toString();
      const status: string = device.status ?? '';

      const matchesSearch: boolean = !normalizedSearch || [
        deviceEui,
        serialNumber,
        model,
        address,
        alarmsCount,
        status,
      ].some((value) =>
        String(value ?? '')
          .toLowerCase()
          .includes(normalizedSearch),
      );

      return matchesStatus && matchesModel && matchesSearch;
    });

    this.totalDeviceCount = this.deviceList.length;
    this.filteredDeviceCount = this.deviceList.length;
    this._setTableRows();
  }

  public closeDeleteDialog: () => void = () => {
    this.isDeleteDialogOpen = false;
  }

  public confirmDeleteDevice: () => void = () => {
    this.isDeleteDialogOpen = false;
    if (!this.hasAccess(AccountRole.ADMIN)) return;

    let deviceId: number | undefined = this.selectedDevice?.id

    if (!deviceId) return;

    this._deviceService.archiveDevice(deviceId)
      .subscribe({
        next: (result: BaseResponseDto) => {
          this._messageService.add({
            severity: 'success',
            summary: this._translateService.instant('common.messages.success.summary'),
            detail: this._translateService.instant('common.messages.device.success.archived'),
          })
          this._deviceService.removeDeviceFromList(deviceId);
        },
        error: (error: HttpErrorResponse) => {
          this._messageService.add({
            severity: 'error',
            summary: this._translateService.instant('common.title.error'),
            detail: error.error?.message ?? error.statusText ?? error.error,
          })
        }
      });
  }
}
